import React, { useEffect, useMemo, useRef, useState } from 'react';
import './shell.css';
import FilterHighlight from './FilterHighlight';

// ===================== โครงหน้าเว็บ: เมนูซ้าย (พับกลุ่มได้) + แถบบน (ค้นหา / แจ้งเตือน / ผู้ใช้) + หัวหน้า =====================
// คอม: เมนูซ้ายติดอยู่ · กด ☰ ซ่อน/แสดง · มือถือ (< 960px): กด ☰ เปิดเมนูเป็นลิ้นชัก เลือกแล้วปิดเอง
const COMPANY = 'บริษัท โฮมเอ็กซ์เพิร์ท เอเชีย จำกัด';
const TONES = 7;   // จำนวนสีกรอบไอคอนเมนู (.hx-tico.t0 – .t6 ใน shell.css)
const store = { get: (k, d) => { try { const v = localStorage.getItem(k); return v == null ? d : JSON.parse(v); } catch { return d; } },
  set: (k, v) => { try { localStorage.setItem(k, JSON.stringify(v)); } catch { /* ไม่เป็นไร */ } } };

export const useNarrow = (px = 960) => {
  const [narrow, setNarrow] = useState(() => window.innerWidth < px);
  useEffect(() => { const f = () => setNarrow(window.innerWidth < px); window.addEventListener('resize', f); return () => window.removeEventListener('resize', f); }, [px]);
  return narrow;
};

// ชื่อเมนู: ขึ้นบรรทัดใหม่ได้เฉพาะตรงช่องว่าง (ไม่ตัดกลางคำ เช่น "ส่งรา/ยงาน") · ข้อความในวงเล็บอยู่ก้อนเดียว · "/ + &" ติดคำหน้า
function chunks(t) {
  const out = []; let cur = '', depth = 0;
  for (const ch of String(t)) {
    if (ch === '(') depth++; if (ch === ')') depth = Math.max(0, depth - 1);
    if (ch === ' ' && depth === 0) { if (cur) out.push(cur); cur = ''; } else cur += ch;
  }
  if (cur) out.push(cur);
  return out.reduce((a, c) => { if (a.length && /^[/+&·-]$/.test(c)) a[a.length - 1] += ' ' + c; else a.push(c); return a; }, []);
}
export const Words = ({ t }) => <>{chunks(t).map((c, i) => <React.Fragment key={i}>{i > 0 && ' '}<span className="nw">{c}</span></React.Fragment>)}</>;

export const Icon = ({ icon }) => (icon === 'LINE' ? <span className="hx-ico"><span className="hx-line-ico">LINE</span></span> : <span className="hx-ico" aria-hidden="true">{icon}</span>);

const Logo = ({ color = '#1a73e8', size = 38 }) => <svg width={size} height={size} viewBox="0 0 40 40" aria-hidden="true">
  <path d="M6 18 L20 6 L34 18" fill="none" stroke={color} strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
  <path d="M10 16 V33 H17 V24 H23 V33 H30 V16" fill="none" stroke={color} strokeWidth="4" strokeLinejoin="round" /></svg>;

// ---------- แบ่งหน้า ----------
export function usePager(rows, deps = [], initial = 20) {
  const [size, setSize] = useState(() => store.get('hx.pageSize', initial)), [page, setPage] = useState(1);
  const pages = Math.max(1, Math.ceil(rows.length / size));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => setPage(1), deps);
  const cur = Math.min(page, pages);
  return { rows: rows.slice((cur - 1) * size, cur * size), page: cur, pages, size, total: rows.length, setPage,
    setSize: (s) => { setSize(s); store.set('hx.pageSize', s); setPage(1); } };
}
export function Pager({ p, unit = 'รายการ' }) {
  if (!p.total) return null;
  const nums = []; const add = (x) => nums.push(x);
  if (p.pages <= 7) for (let i = 1; i <= p.pages; i++) add(i);
  else { add(1); if (p.page > 4) add('…a'); for (let i = Math.max(2, p.page - 2); i <= Math.min(p.pages - 1, p.page + 2); i++) add(i); if (p.page < p.pages - 3) add('…b'); add(p.pages); }
  return <div className="hx-pager">
    <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}>แสดง <select value={p.size} onChange={(e) => p.setSize(Number(e.target.value))} aria-label="จำนวนต่อหน้า">
      {[20, 50, 100, 500].map((n) => <option key={n} value={n}>{n}</option>)}</select> {unit}ต่อหน้า</label>
    <span>รวม {p.total.toLocaleString()} {unit}</span>
    {p.pages > 1 && <div className="pg" role="navigation" aria-label="เลือกหน้า">
      <button className="nav" disabled={p.page === 1} onClick={() => p.setPage(p.page - 1)} aria-label="หน้าก่อน">‹</button>
      {nums.map((x) => (typeof x === 'string' ? <button key={x} disabled>…</button>
        : <button key={x} className={x === p.page ? 'on' : ''} aria-current={x === p.page ? 'page' : undefined} onClick={() => p.setPage(x)}>{x}</button>))}
      <button className="nav" disabled={p.page === p.pages} onClick={() => p.setPage(p.page + 1)} aria-label="หน้าถัดไป">›</button></div>}
  </div>;
}

// ---------- หัวหน้า ----------
export const PageHead = ({ icon, title, sub, children, back }) => <div className="hx-head">
  {back}
  <div className="tile">{icon === 'LINE' ? <span className="hx-line-ico" style={{ width: 34, height: 30, fontSize: 11 }}>LINE</span> : icon}</div>
  <div style={{ minWidth: 0 }}><h1>{title}</h1>{sub && <div className="sub">{sub}</div>}</div>
  {children && <div className="acts">{children}</div>}
</div>;

// ---------- ป๊อปอัปปิดเมื่อคลิกข้างนอก ----------
function useOutside(open, setOpen) {
  const ref = useRef(null);
  useEffect(() => { if (!open) return undefined; const f = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const k = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', f); document.addEventListener('keydown', k);
    return () => { document.removeEventListener('mousedown', f); document.removeEventListener('keydown', k); }; }, [open, setOpen]);
  return ref;
}

// ---------- ค้นหาเมนู / รหัสสินค้า ----------
function TopSearch({ items, stock, go, openItem }) {
  const [q, setQ] = useState(''), [open, setOpen] = useState(false), [act, setAct] = useState(0);
  const ref = useOutside(open, setOpen);
  const term = q.trim().toLowerCase();
  const res = useMemo(() => {
    if (!term) return [];
    const m = items.filter(([, , label]) => label.toLowerCase().includes(term)).slice(0, 5).map(([k, icon, label]) => ({ key: 'm' + k, icon, label, run: () => go(k) }));
    const s = (stock || []).filter((r) => `${r.good_code} ${r.good_name || ''}`.toLowerCase().includes(term)).slice(0, 8)
      .map((r) => ({ key: 's' + r.good_code, icon: '📦', label: r.good_code, sub: r.good_name, run: () => openItem(r.good_code) }));
    return [...m, ...s];
  }, [term, items, stock, go, openItem]);
  const pick = (r) => { r.run(); setQ(''); setOpen(false); };
  return <div className="hx-search" ref={ref}>
    <span className="s-ico">🔍</span>
    <input value={q} placeholder="ค้นหาเมนู หรือ รหัสสินค้า…" aria-label="ค้นหาเมนู หรือ รหัสสินค้า" onFocus={() => setOpen(true)}
      onChange={(e) => { setQ(e.target.value); setOpen(true); setAct(0); }}
      onKeyDown={(e) => { if (e.key === 'ArrowDown') { e.preventDefault(); setAct((a) => Math.min(a + 1, res.length - 1)); } if (e.key === 'ArrowUp') { e.preventDefault(); setAct((a) => Math.max(a - 1, 0)); }
        if (e.key === 'Enter' && res[act]) pick(res[act]); }} />
    {open && term && <div className="hx-pop" style={{ left: 0, right: 0, minWidth: 280 }}>
      {!res.length ? <div className="ph">ไม่พบเมนูหรือรหัสสินค้าที่ตรงกับ “{q}”</div> : res.map((r, i) => <button key={r.key} className={i === act ? 'act' : ''} onClick={() => pick(r)}>
        <Icon icon={r.icon} /><span style={{ minWidth: 0 }}><b style={{ fontWeight: r.sub ? 700 : 500 }}>{r.label}</b>{r.sub && <div style={{ fontSize: 12, color: '#64748b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.sub}</div>}</span></button>)}
    </div>}
  </div>;
}

function Bell({ notes, go }) {
  const [open, setOpen] = useState(false); const ref = useOutside(open, setOpen);
  const total = notes.reduce((t, x) => t + x.n, 0);
  return <div style={{ position: 'relative' }} ref={ref}>
    <button className="hx-iconbtn" onClick={() => setOpen((o) => !o)} aria-label={`แจ้งเตือน ${total} รายการ`} aria-expanded={open}>🔔{total > 0 && <span className="hx-dot" />}</button>
    {open && <div className="hx-pop" style={{ right: 0, width: 300, maxWidth: '92vw' }}>
      <div className="ph">แจ้งเตือน</div>
      {!notes.some((x) => x.n) ? <div style={{ padding: '6px 10px 10px', fontSize: 14, color: '#0b7a3e' }}>✅ ไม่มีงานค้าง</div>
        : notes.filter((x) => x.n).map((x) => <button key={x.key} onClick={() => { go(x.go || x.key); setOpen(false); }}><Icon icon={x.icon} /><span style={{ flex: 1 }}>{x.label}</span><span className="hx-badge">{x.n}</span></button>)}
    </div>}
  </div>;
}

function UserMenu({ me, email, onChangePw, onSignOut }) {
  const [open, setOpen] = useState(false); const ref = useOutside(open, setOpen);
  const name = me?.display_name || email || 'ผู้ใช้';
  const role = String(me?.role || '').toUpperCase() === 'ADMIN' ? 'Admin' : name;
  return <div style={{ position: 'relative' }} ref={ref}>
    <button className="hx-user" onClick={() => setOpen((o) => !o)} aria-expanded={open} aria-label="เมนูผู้ใช้">
      <span className="hx-avatar">{(name || '?').trim().charAt(0).toUpperCase()}</span>
      <span className="txt"><div className="nm">{name}{role === 'Admin' && <span className="hx-role">Admin</span>}</div><div className="co">{COMPANY}</div></span><span className="txt" style={{ fontSize: 12 }}>▾</span>
    </button>
    {open && <div className="hx-pop" style={{ right: 0, width: 250 }}>
      <div className="ph" style={{ fontWeight: 400 }}><b style={{ color: '#1f2a44' }}>{name}</b><div>{email}</div></div>
      <button onClick={() => { setOpen(false); onChangePw(); }}>🔑 เปลี่ยนรหัสผ่าน</button>
      <button onClick={() => { setOpen(false); onSignOut(); }}>↪ ออกจากระบบ</button>
    </div>}
  </div>;
}

// ---------- โครงหน้า ----------
export default function Shell({ groups, section, go, count, me, email, onChangePw, onSignOut, stock, openItem, notes, children }) {
  const narrow = useNarrow();
  const [hidden, setHidden] = useState(() => store.get('hx.sideHidden', false)), [drawer, setDrawer] = useState(false);
  const toggleSide = () => (narrow ? setDrawer((o) => !o) : setHidden((h) => { store.set('hx.sideHidden', !h); return !h; }));
  const pick = (k) => { go(k); if (narrow) setDrawer(false); };
  useEffect(() => { if (!narrow) setDrawer(false); }, [narrow]);
  const flat = groups.flatMap((g) => g.items);
  const badge = (k) => (count[k] > 0 ? <span className="hx-badge">{count[k]}</span> : null);
  // เมนู 2 ชั้น (แบบ Central Postal / Sales Dashboard): แถบซ้ายสุด = หัวข้อหลัก → แผงขาว = เมนูย่อยของหัวข้อที่เลือก
  const gid = (g) => g.id || 'top';
  const curG = groups.find((g) => g.items.some(([k]) => k === section)) || groups[0];
  const [railSel, setRailSel] = useState(null);
  useEffect(() => setRailSel(null), [section]);   // เปลี่ยนหน้าแล้ว แผงขาวตามหัวข้อของหน้านั้น
  const selG = groups.find((g) => gid(g) === railSel) || curG;
  const selIdx = Math.max(0, groups.indexOf(selG));
  return <div className={`hx-app${hidden && !narrow ? ' side-hidden' : ''}${drawer ? ' drawer-open' : ''}`}>
    <FilterHighlight />
    <aside className="hx-side" aria-label="เมนูหลัก">
      <nav className="hx-rail" aria-label="หัวข้อหลัก">
        <div className="hx-brand"><Logo color="#ffffff" size={34} /><div><b>HomeXpert</b><span>คลังสินค้า &amp; ผลิต</span></div></div>
        {groups.map((g) => { const n = g.items.reduce((t, [k]) => t + (count[k] || 0), 0); const on = gid(g) === gid(selG);
          return <button key={gid(g)} className={`hx-pill${on ? ' on' : ''}${g === curG ? ' cur' : ''}`} aria-pressed={on} onClick={() => setRailSel(gid(g))}>
            <span className="pi" aria-hidden="true">{g.icon || '🏠'}</span><span className="pl"><Words t={g.label || 'หน้าแรก'} /></span>{n > 0 && <span className="hx-badge">{n}</span>}</button>; })}
      </nav>
      <div className="hx-panel">
        {narrow && <button className="hx-iconbtn" style={{ alignSelf: 'flex-end', margin: '6px 6px 0' }} onClick={() => setDrawer(false)} aria-label="ปิดเมนู">✕</button>}
        <nav className="hx-nav" aria-label={selG.label || 'หน้าแรก'}>
          <div className="hx-sec">{selG.label || 'หน้าแรก'}</div>
          {selG.items.map(([k, icon, label], i) => <button key={k} className={`hx-item${section === k ? ' on' : ''}`} aria-current={section === k ? 'page' : undefined} onClick={() => pick(k)}>
            <span className={`hx-tico t${(selIdx + i) % TONES}`}><Icon icon={icon} /></span><span style={{ flex: 1, minWidth: 0 }}><Words t={label} /></span>{badge(k)}</button>)}
        </nav>
      </div>
    </aside>
    <div className="hx-scrim" onClick={() => setDrawer(false)} />
    <div className="hx-main">
      <header className="hx-top">
        <button className="hx-iconbtn" onClick={toggleSide} aria-label="เปิด/ปิดเมนู">☰</button>
        <TopSearch items={flat} stock={stock} go={pick} openItem={openItem} />
        <span style={{ flex: 1 }} />
        <Bell notes={notes} go={pick} />
        <span className="hx-sep" />
        <UserMenu me={me} email={email} onChangePw={onChangePw} onSignOut={onSignOut} />
      </header>
      <main className="hx-content">{children}</main>
    </div>
  </div>;
}
